// 📝 사용자 설문 클라이언트 (2026-09-11) — 서버 routes/survey.ts 짝.
//   useSurveyState(): 내 상태(보임/응답 여부/관리 권한) — 로그인 후 1회 조회 + 응답/설정 변경 시 갱신.
//   공개 범위(owner 전용 → 전체)는 서버가 판정해 visible 로 내려준다 — 클라는 판정 로직을 갖지 않는다.
import { useEffect, useState } from 'react';
import { useAuthStore } from '../stores/authStore';

export interface SurveyState { visible: boolean; answered: boolean; canManage: boolean; enabled: boolean; audience: 'owner' | 'all'; version: number; round: number; endsAt: string | null; closed: boolean; daysLeft: number | null }
export interface SurveySettings { enabled: boolean; audience: 'owner' | 'all'; round: number; roundStartedAt: string | null; endsAt: string | null; closed?: boolean; daysLeft?: number | null; ownerUsers: string[]; ownerNames?: string[]; updatedAt: string | null; updatedBy: string | null }
/** 🤖 AI 분석 결과(회차별 1건, 서버 tpt_survey_analysis) */
export interface SurveyAnalysis { text: string; at: string; n: number; provider?: string; lang?: string }
export interface SurveyResponseRow { company: string; dept: string; parentDept: string; title: string; position: string; answers: Record<string, unknown>; at: string; lang?: string; appVersion?: string; version?: number }
export interface SurveyResults { settings: SurveySettings; version: number; round: number; rounds: number[]; total: number; responses: SurveyResponseRow[] }

const hdr = (): Record<string, string> => {
	const { authToken, userId } = useAuthStore.getState();
	return { 'Content-Type': 'application/json', ...(authToken && userId ? { 'X-Auth-Token': authToken, 'X-User-Id': userId } : {}) };
};

let cache: SurveyState | null = null;
let fetchedFor = '';
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((fn) => { try { fn(); } catch { /* */ } });

export async function syncSurveyState(force = false): Promise<SurveyState | null> {
	const { authToken, userId } = useAuthStore.getState();
	if (!authToken || !userId) { cache = null; return null; }
	const key = userId;
	if (!force && cache && fetchedFor === key) return cache;
	if (inflight) { await inflight; return cache; }
	inflight = (async () => {
		try {
			const r = await fetch('/api/tpt/survey', { headers: hdr() });
			if (!r.ok) { cache = null; return; }
			cache = await r.json() as SurveyState;
			fetchedFor = key;
			notify();
		} catch { cache = null; } finally { inflight = null; }
	})();
	await inflight;
	return cache;
}

/** 리액티브 — 마운트 시 조회, 응답/설정 변경 시 재렌더. 미로그인·실패 = null(아무것도 안 보임). */
export function useSurveyState(): SurveyState | null {
	const authToken = useAuthStore((s) => s.authToken);
	const [, tick] = useState(0);
	useEffect(() => {
		const fn = () => tick((n) => n + 1);
		listeners.add(fn);
		void syncSurveyState();
		return () => { listeners.delete(fn); };
	}, [authToken]);
	return authToken ? cache : null;
}

export async function submitSurvey(answers: Record<string, unknown>, lang: string): Promise<void> {
	const r = await fetch('/api/tpt/survey', { method: 'POST', headers: hdr(), body: JSON.stringify({ answers, lang, appVersion: __APP_VERSION__ }) });
	const j = await r.json().catch(() => ({})) as { error?: string; errors?: Array<{ id: string; code: string }> };
	if (!r.ok) throw new Error(j.errors?.length ? `invalid: ${j.errors.map((e) => `${e.id}(${e.code})`).join(', ')}` : (j.error || `HTTP ${r.status}`));
	await syncSurveyState(true);
}

export async function deleteMySurvey(): Promise<void> {
	const r = await fetch('/api/tpt/survey?mine=1', { method: 'DELETE', headers: hdr() });
	if (!r.ok) throw new Error(`HTTP ${r.status}`);
	await syncSurveyState(true);
}

export async function fetchSurveyResults(round?: number): Promise<SurveyResults> {
	const r = await fetch(`/api/tpt/survey?results=1${round ? `&round=${round}` : ''}`, { headers: hdr() });
	const j = await r.json().catch(() => ({})) as SurveyResults & { error?: string; ownerOnly?: boolean };
	if (!r.ok) throw new Error(j.ownerOnly ? 'ownerOnly' : (j.error || `HTTP ${r.status}`));
	return j;
}

export async function updateSurveySettings(patch: { enabled?: boolean; audience?: 'owner' | 'all'; newRound?: true; endsAt?: string | null }): Promise<SurveySettings> {
	const r = await fetch('/api/tpt/survey?settings=1', { method: 'PUT', headers: hdr(), body: JSON.stringify(patch) });
	const j = await r.json().catch(() => ({})) as { settings?: SurveySettings; error?: string };
	if (!r.ok || !j.settings) throw new Error(j.error || `HTTP ${r.status}`);
	await syncSurveyState(true);
	return j.settings;
}

/** 🤖 저장된 AI 분석(회차) — 없으면 null */
export async function fetchSurveyAnalysis(round?: number): Promise<{ round: number; analysis: SurveyAnalysis | null }> {
	const r = await fetch(`/api/tpt/survey?analysis=1${round ? `&round=${round}` : ''}`, { headers: hdr() });
	const j = await r.json().catch(() => ({})) as { round?: number; analysis?: SurveyAnalysis | null; error?: string; ownerOnly?: boolean };
	if (!r.ok) throw new Error(j.ownerOnly ? 'ownerOnly' : (j.error || `HTTP ${r.status}`));
	return { round: Number(j.round) || 0, analysis: j.analysis || null };
}

/** 🤖 AI 분석 생성 — 서버가 익명 집계만 LLM 에 보낸다(개인 행 없음). labels = 현재 UI 언어의 문항·선택지 문구(서버는 로케일을 모른다). 수십 초 걸릴 수 있다. */
export async function runSurveyAnalysis(round: number, lang: string, labels: { q: Record<string, string>; o: Record<string, Record<string, string>> }): Promise<SurveyAnalysis> {
	const r = await fetch(`/api/tpt/survey?analyze=1&round=${round}`, { method: 'POST', headers: hdr(), body: JSON.stringify({ lang, labels }) });
	const j = await r.json().catch(() => ({})) as { analysis?: SurveyAnalysis; error?: string };
	if (!r.ok || !j.analysis) throw new Error(j.error || `HTTP ${r.status}`);
	return j.analysis;
}
